import { merchantApi } from '@/config/axios'

export interface PracticeCustomerVO {
  id?: number
  firmId?: number
  customerNo?: string
  customerType: string
  name: string
  mobile?: string
  category?: string
  blacklisted?: boolean
  remark?: string
  createTime?: string
}

export interface PracticeMatterVO {
  id?: number
  firmCaseNo?: string
  caseNo?: string
  title: string
  caseType: string
  customerId: number
  customerName?: string
  leadLawyerId: number
  leadLawyerName?: string
  practiceStatus?: string
  source?: string
  createTime?: string
  nextStatuses?: string[]
}

export interface PracticeDashboardVO {
  todayHearingCount?: number
  upcomingDeadlineCount?: number
  overdueTaskCount?: number
  overdueDeadlineCount?: number
  pendingTimeEntryCount?: number
  openReceivableCount?: number
  outstandingAmount?: number
}

export const FirmPracticeApi = {
  customerPage: (params: Record<string, any>) =>
    merchantApi.get<{ list: PracticeCustomerVO[]; total: number }>({
      url: '/firm-api/practice/customer/page',
      params
    }),
  customerGet: (id: number) =>
    merchantApi.get<PracticeCustomerVO>({ url: '/firm-api/practice/customer/get', params: { id } }),
  customerCreate: (data: Partial<PracticeCustomerVO> & { customerType: string; name: string }) =>
    merchantApi.post<number>({ url: '/firm-api/practice/customer/create', data }),
  customerUpdate: (data: Partial<PracticeCustomerVO> & { id: number; customerType: string; name: string }) =>
    merchantApi.put<boolean>({ url: '/firm-api/practice/customer/update', data }),

  matterPage: (params: Record<string, any>) =>
    merchantApi.get<{ list: PracticeMatterVO[]; total: number }>({
      url: '/firm-api/practice/matter/page',
      params
    }),
  matterGet: (id: number) =>
    merchantApi.get<PracticeMatterVO>({ url: '/firm-api/practice/matter/get', params: { id } }),
  matterCreate: (data: {
    title: string
    caseType: string
    customerId: number
    leadLawyerId: number
    description?: string
  }) => merchantApi.post<number>({ url: '/firm-api/practice/matter/create', data }),
  matterTransition: (data: { id: number; toStatus: string; remark?: string }) =>
    merchantApi.put<boolean>({ url: '/firm-api/practice/matter/transition', data }),

  dashboardSummary: (focusLimit = 10) =>
    merchantApi.get<PracticeDashboardVO>({
      url: '/firm-api/practice/dashboard/summary',
      params: { focusLimit }
    }),

  timePage: (params: Record<string, any>) =>
    merchantApi.get<{ list: PracticeTimeVO[]; total: number }>({
      url: '/firm-api/practice/time-entry/page',
      params
    }),
  timeCreate: (data: {
    caseId: number
    lawyerId: number
    workDate: string
    minutes: number
    workType: string
    content: string
    billable?: boolean
  }) => merchantApi.post<number>({ url: '/firm-api/practice/time-entry/create', data }),
  timeSubmit: (id: number) =>
    merchantApi.put<boolean>({ url: '/firm-api/practice/time-entry/submit', data: { id } }),
  timeApprove: (data: { id: number; passed: boolean; rejectReason?: string }) =>
    merchantApi.put<boolean>({ url: '/firm-api/practice/time-entry/approve', data }),

  dossierPage: (params: Record<string, any>) =>
    merchantApi.get<{ list: PracticeDossierVO[]; total: number }>({
      url: '/firm-api/practice/dossier/page',
      params
    }),
  dossierCreate: (data: {
    caseId: number
    category: string
    title: string
    fileUrl: string
    fileName?: string
    remark?: string
  }) => merchantApi.post<number>({ url: '/firm-api/practice/dossier/create', data }),
  dossierDelete: (id: number) =>
    merchantApi.delete<boolean>({ url: '/firm-api/practice/dossier/delete', params: { id } }),

  receivablePage: (params: Record<string, any>) =>
    merchantApi.get<{ list: PracticeReceivableVO[]; total: number }>({
      url: '/firm-api/practice/fee/receivable/page',
      params
    }),
  receivableCreate: (data: {
    caseId: number
    feeType: string
    amount: number
    title?: string
    customerId?: number
    dueDate?: string
    remark?: string
  }) => merchantApi.post<number>({ url: '/firm-api/practice/fee/receivable/create', data }),
  receivableOpen: (id: number) =>
    merchantApi.put<boolean>({ url: '/firm-api/practice/fee/receivable/open', params: { id } }),

  receiptPage: (params: Record<string, any>) =>
    merchantApi.get<{ list: PracticeReceiptVO[]; total: number }>({
      url: '/firm-api/practice/fee/receipt/page',
      params
    }),
  receiptCreate: (data: {
    caseId: number
    receivableId: number
    amount: number
    payTime: string
    payMethod?: string
    remark?: string
  }) => merchantApi.post<number>({ url: '/firm-api/practice/fee/receipt/create', data }),

  hearingPage: (params: Record<string, any>) =>
    merchantApi.get<{ list: PracticeHearingVO[]; total: number }>({
      url: '/firm-api/practice/hearing/page',
      params
    }),
  hearingCreate: (data: {
    caseId: number
    hearingTime: string
    location?: string
    courtRoom?: string
    judgeName?: string
    remark?: string
  }) => merchantApi.post<number>({ url: '/firm-api/practice/hearing/create', data }),
  hearingUpdate: (data: {
    id: number
    caseId: number
    hearingTime: string
    location?: string
    courtRoom?: string
    judgeName?: string
    remark?: string
  }) => merchantApi.put<boolean>({ url: '/firm-api/practice/hearing/update', data }),
  hearingComplete: (data: { id: number; result?: string }) =>
    merchantApi.put<boolean>({ url: '/firm-api/practice/hearing/complete', data }),
  hearingCancel: (id: number) =>
    merchantApi.put<boolean>({ url: '/firm-api/practice/hearing/cancel', params: { id } }),

  taskPage: (params: Record<string, any>) =>
    merchantApi.get<{ list: PracticeTaskVO[]; total: number }>({
      url: '/firm-api/practice/task/page',
      params
    }),
  taskCreate: (data: {
    caseId: number
    title: string
    content?: string
    assigneeLawyerId: number
    dueDate?: string
    remark?: string
  }) => merchantApi.post<number>({ url: '/firm-api/practice/task/create', data }),
  taskUpdate: (data: {
    id: number
    caseId: number
    title: string
    content?: string
    assigneeLawyerId: number
    dueDate?: string
    remark?: string
  }) => merchantApi.put<boolean>({ url: '/firm-api/practice/task/update', data }),
  taskComplete: (id: number) =>
    merchantApi.put<boolean>({ url: '/firm-api/practice/task/complete', params: { id } }),
  taskCancel: (id: number) =>
    merchantApi.put<boolean>({ url: '/firm-api/practice/task/cancel', params: { id } }),

  deadlinePage: (params: Record<string, any>) =>
    merchantApi.get<{ list: PracticeDeadlineVO[]; total: number }>({
      url: '/firm-api/practice/deadline/page',
      params
    }),
  deadlineCreate: (data: {
    caseId: number
    deadlineType: string
    title: string
    dueDate: string
    remindDaysBefore?: number
    remark?: string
  }) => merchantApi.post<number>({ url: '/firm-api/practice/deadline/create', data }),
  deadlineUpdate: (data: {
    id: number
    caseId: number
    deadlineType: string
    title: string
    dueDate: string
    remindDaysBefore?: number
    remark?: string
  }) => merchantApi.put<boolean>({ url: '/firm-api/practice/deadline/update', data }),
  deadlineComplete: (id: number) =>
    merchantApi.put<boolean>({ url: '/firm-api/practice/deadline/complete', params: { id } }),
  deadlineDelete: (id: number) =>
    merchantApi.delete<boolean>({ url: '/firm-api/practice/deadline/delete', params: { id } }),

  alertSummary: () =>
    merchantApi.get<PracticeAlertSummaryVO>({ url: '/firm-api/practice/alert/summary' }),
  alertList: (limit = 30) =>
    merchantApi.get<PracticeAlertItemVO[]>({
      url: '/firm-api/practice/alert/list',
      params: { limit }
    })
}

export interface PracticeTimeVO {
  id?: number
  caseId: number
  caseTitle?: string
  firmCaseNo?: string
  lawyerId?: number
  lawyerName?: string
  workDate: string
  minutes: number
  workType: string
  content?: string
  billable?: boolean
  status?: string
  amountSnapshot?: number
  createTime?: string
}

export interface PracticeDossierVO {
  id?: number
  caseId: number
  caseTitle?: string
  firmCaseNo?: string
  category: string
  title: string
  fileUrl: string
  fileName?: string
  versionNo?: number
  isCurrent?: boolean
  createTime?: string
}

export interface PracticeReceivableVO {
  id?: number
  caseId: number
  caseTitle?: string
  firmCaseNo?: string
  customerId?: number
  customerName?: string
  feeType: string
  title?: string
  amount: number
  receivedAmount?: number
  outstandingAmount?: number
  status?: string
  dueDate?: string
  createTime?: string
}

export interface PracticeReceiptVO {
  id?: number
  caseId: number
  caseTitle?: string
  receivableId: number
  receivableTitle?: string
  amount: number
  payTime: string
  payMethod?: string
  status?: string
  createTime?: string
}

export interface PracticeHearingVO {
  id?: number
  caseId: number
  caseTitle?: string
  firmCaseNo?: string
  hearingTime: string
  location?: string
  courtRoom?: string
  judgeName?: string
  result?: string
  status?: string
  remark?: string
  createTime?: string
}

export interface PracticeTaskVO {
  id?: number
  caseId: number
  caseTitle?: string
  firmCaseNo?: string
  title: string
  content?: string
  assigneeLawyerId: number
  assigneeLawyerName?: string
  dueDate?: string
  status?: string
  completedTime?: string
  remark?: string
  createTime?: string
}

export interface PracticeDeadlineVO {
  id?: number
  caseId: number
  caseTitle?: string
  firmCaseNo?: string
  deadlineType: string
  title: string
  dueDate: string
  remindDaysBefore?: number
  status?: string
  completedTime?: string
  remark?: string
  createTime?: string
}

export interface PracticeAlertSummaryVO {
  todayHearingCount?: number
  upcomingDeadlineCount?: number
  overdueTaskCount?: number
  overdueDeadlineCount?: number
  expiringContractCount?: number
}

export interface PracticeAlertItemVO {
  alertType: string
  refId?: number
  caseId?: number
  caseTitle?: string
  firmCaseNo?: string
  title?: string
  eventTime?: string
  dueDate?: string
  status?: string
}
